import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { getNews } from "@/lib/api";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function News() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getNews()
      .then((data) => setNews(data))
      .catch(() => setNews([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="news"
      data-testid="news-section"
      className="relative py-24 lg:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">
              ⌁ Dispatch
            </p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">
              Latest <span className="gold-text">Dispatch</span>
            </h2>
          </div>
          <p className="max-w-md text-zinc-400">
            Drops, signings, studio updates, and post-match analysis from the
            TGL newsroom.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="tgl-card cut-corners h-72 lg:col-span-6 animate-pulse"
                />
              ))
            : news.map((n, i) => {
                const span =
                  i === 0
                    ? "lg:col-span-7 lg:row-span-2 h-[28rem] lg:h-[36rem]"
                    : "lg:col-span-5 h-72";
                return (
                  <motion.article
                    key={n.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: i * 0.06 }}
                    className={`tgl-card cut-corners group relative overflow-hidden ${span}`}
                    data-testid={`news-card-${n.id}`}
                  >
                    <img
                      src={n.image}
                      alt={n.title}
                      className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                        n.coming_soon ? "opacity-40 blur-md scale-110" : "opacity-70"
                      }`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />

                    {n.coming_soon && (
                      <div
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                        data-testid={`news-coming-soon-${n.id}`}
                      >
                        <div className="text-center px-6">
                          <div className="inline-block border border-[#D4AF37] bg-black/60 backdrop-blur-sm px-5 py-2 mb-4 cut-corners">
                            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#D4AF37]">
                              May 2026
                            </span>
                          </div>
                          <div
                            className="font-display font-black uppercase tracking-tighter gold-text text-5xl sm:text-6xl lg:text-7xl leading-none"
                            style={{
                              filter:
                                "drop-shadow(0 0 14px rgba(212,175,55,0.55)) drop-shadow(0 0 28px rgba(212,175,55,0.3))",
                            }}
                          >
                            Coming
                            <br />
                            Soon
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="absolute inset-0 p-6 lg:p-8 flex flex-col justify-between">
                      <div className="flex justify-between items-start">
                        <span
                          className={`text-[10px] font-bold uppercase tracking-[0.3em] bg-black/60 border px-2 py-1 ${
                            n.coming_soon
                              ? "text-[#FFE9A0] border-[#D4AF37]"
                              : "text-[#D4AF37] border-[#D4AF37]/30"
                          }`}
                        >
                          {n.category}
                        </span>
                        {!n.coming_soon && (
                          <ArrowUpRight
                            size={20}
                            className="text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity"
                          />
                        )}
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                          {formatDate(n.published_at)}
                        </p>
                        <h3 className="mt-2 font-display text-2xl lg:text-3xl font-black uppercase text-white">
                          {n.title}
                        </h3>
                        <p className="mt-3 text-sm text-zinc-300 max-w-xl">
                          {n.excerpt}
                        </p>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
        </div>
      </div>
    </section>
  );
}
